import { Inject, Injectable, OnDestroy, Optional, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformServer } from '@angular/common';

import { DeviceType } from '@seb/web-design-system/core';

import { lBreakpoint, mBreakpoint, xlBreakpoint } from '../utils/device.utils';
import { SERVER_DEVICE_TYPE } from '../sd-lib-config';

@Injectable({
  providedIn: 'root'
})
export class DeviceService implements OnDestroy {
  private readonly destroy$: Subject<void> = new Subject();

  private _isMobile = new BehaviorSubject<boolean>(false);
  private _isTablet = new BehaviorSubject<boolean>(false);
  private _isDevice = new BehaviorSubject<boolean>(false);
  private _isDesktop = new BehaviorSubject<boolean>(false);
  private _isDesktopLarge = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    breakpointObserver: BreakpointObserver,
    @Optional() @Inject(SERVER_DEVICE_TYPE) private serverDevice: DeviceType,
  ) {
    if (isPlatformServer(this.platformId)) {
      if (this.serverDevice === 'mobile') {
        this._isMobile.next(true);
        this._isDevice.next(true);
      } else if (this.serverDevice === 'tablet') {
        this._isTablet.next(true);
        this._isDevice.next(true);
      } else {
        this._isDesktop.next(true);
      }
      return;
    }

    breakpointObserver.observe(`(max-width:${mBreakpoint - 1}px)`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this._isMobile.next(state.matches));

    breakpointObserver.observe(`(min-width:${mBreakpoint}px) and (max-width:${lBreakpoint - 1}px)`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this._isTablet.next(state.matches));

    breakpointObserver.observe(`(max-width:${lBreakpoint - 1}px)`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this._isDevice.next(state.matches));

    breakpointObserver.observe(`(min-width:${lBreakpoint}px) and (max-width:${xlBreakpoint - 1}px)`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this._isDesktop.next(state.matches));

    breakpointObserver.observe(`(min-width:${xlBreakpoint}px)`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this._isDesktopLarge.next(state.matches));
  }

  get isMobile() {
    return this._isMobile.asObservable();
  }

  get isTablet() {
    return this._isTablet.asObservable();
  }

  get isDevice() {
    return this._isDevice.asObservable();
  }

  get isDesktop() {
    return this._isDesktop.asObservable();
  }

  get isDesktopLarge() {
    return this._isDesktopLarge.asObservable();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
